import {useCAPIQuery} from './useCAPIQuery'
import {SessionState, type Session} from '../types/session'
import {useCurrentRepository} from '@github-ui/current-repository'
import {usePullContext} from '../contexts/PullContext'
import {fetchCAPI} from '../utils/fetch-capi'
import {useState} from 'react'

export const CONSECUTIVE_FAILURE_TOLERANCE = 10

interface UseAgentSessionsProps {
  apiURL?: string | null
  initialData: Session[] | undefined
  sessionsPollingInterval: number
}

export function useSessions({apiURL, initialData, sessionsPollingInterval}: UseAgentSessionsProps) {
  const repo = useCurrentRepository()
  const {
    pull: {id: pullId},
  } = usePullContext()
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const [timedOut, setHasTimedOut] = useState(false)
  const isRetrying = consecutiveFailures > 0 && !timedOut

  const query = useCAPIQuery<Session[]>({
    queryKey: ['agent-sessions', repo.ownerLogin, repo.name, pullId, apiURL],
    queryFn: async () => {
      try {
        const response = await fetchCAPI({
          apiURL,
          path: `/agents/sessions/resource/pull/${pullId}`,
        })
        const data = await response.json()
        const sessions = data.sessions as Session[]

        // Reset consecutive failures on successful fetch
        setConsecutiveFailures(0)
        setHasTimedOut(false)

        return sessions.sort((a, b) => {
          // Sort sessions by creation date, most recent first
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
      } catch (error) {
        // Increment consecutive failures on error
        setConsecutiveFailures(prev => {
          const newCount = prev + 1
          if (newCount >= CONSECUTIVE_FAILURE_TOLERANCE) {
            setHasTimedOut(true)
          }
          return newCount
        })
        throw error // Re-throw to maintain React Query error state
      }
    },
    initialData,
    refetchInterval: capiQuery => {
      const data = capiQuery.state.data
      // If no data yet, use default interval
      if (!data) return sessionsPollingInterval

      // Check if any sessions are in active/running state where we want to poll at the default rate
      const hasActiveSessions = data.some(
        session =>
          session.state === SessionState.InProgress ||
          session.state === SessionState.Idle ||
          session.state === SessionState.WaitingForUser ||
          session.state === SessionState.Queued,
      )

      // Slow down polling by a factor of 10 if there are no active sessions. Will speed back up
      // as soon as a session becomes active.
      return hasActiveSessions ? sessionsPollingInterval : sessionsPollingInterval * 10
    },
    // Stop polling after consecutive failure tolerance is reached
    enabled: consecutiveFailures < CONSECUTIVE_FAILURE_TOLERANCE,
  })

  return {
    ...query,
    timedOut,
    isRetrying,
  }
}
